import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSearch, FaSort, FaFilter } from "react-icons/fa";
import ProductCard from "../components/ProductCard";
import { ProductGridSkeleton } from "../components/HomeSkeletons";
import { fetchAllProductsWithSearch } from "../actions/ProductActions";
import { fetchAdvancedSearch } from "../actions/FilterActions";
import "../styles/AllProducts.css";

const AllProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { products, loading, count } = useSelector((state) => state.products);
  const {
    results: advancedSearchResults = [],
    totalResults: advancedSearchCount = 0,
    loading: advancedSearchLoading = false,
  } = useSelector((state) => state.advancedSearch || {});

  // Initialize state from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page")) || 1
  );
  const [productsPerPage, setProductsPerPage] = useState(
    parseInt(searchParams.get("perPage")) || 24
  );
  const [sortBy, setSortBy] = useState(
    searchParams.get("sort") || "created_at"
  );
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [displayResults, setDisplayResults] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [paginatedResults, setPaginatedResults] = useState([]);

  const totalPages = Math.ceil(
    (isSearchMode ? advancedSearchCount : count) / productsPerPage
  );

  const sortOptions = [
    { value: "created_at", label: "Newest First" },
    { value: "-created_at", label: "Oldest First" },
    { value: "name", label: "Name A-Z" },
    { value: "-name", label: "Name Z-A" },
    { value: "discounted_price", label: "Price Low to High" },
    { value: "-discounted_price", label: "Price High to Low" },
  ];

  const productsPerPageOptions = [12, 24, 48, 96];

  // Function to update URL parameters
  const updateURL = (params) => {
    const newSearchParams = new URLSearchParams(location.search);

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        newSearchParams.set(key, params[key]);
      } else {
        newSearchParams.delete(key);
      }
    });

    navigate(`/all-products?${newSearchParams.toString()}`, { replace: true });
  };

  // Initial load effect
  useEffect(() => {
    dispatch(
      fetchAllProductsWithSearch(currentPage, productsPerPage, "", sortBy)
    );
  }, [dispatch]);

  // Handle URL parameter changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlPage = parseInt(searchParams.get("page")) || 1;
    const urlPerPage = parseInt(searchParams.get("perPage")) || 24;
    const urlSort = searchParams.get("sort") || "created_at";
    const urlQuery = searchParams.get("q") || "";

    // Update state if URL parameters are different
    if (urlPage !== currentPage) setCurrentPage(urlPage);
    if (urlPerPage !== productsPerPage) setProductsPerPage(urlPerPage);
    if (urlSort !== sortBy) setSortBy(urlSort);
    if (urlQuery !== searchTerm) setSearchTerm(urlQuery);

    // If there's a search query, trigger search
    if (urlQuery.trim()) {
      setIsSearchMode(true);
      dispatch(
        fetchAdvancedSearch({
          search: urlQuery,
          max_results: 1000,
          fields: ["name", "brand", "description", "keywords", "tags"],
        })
      );
    } else {
      setIsSearchMode(false);
      dispatch(fetchAllProductsWithSearch(urlPage, urlPerPage, "", urlSort));
    }
  }, [location.search]);

  // Handle regular product loading (non-search)
  useEffect(() => {
    if (!isSearchMode) {
      dispatch(
        fetchAllProductsWithSearch(currentPage, productsPerPage, "", sortBy)
      );
    }
  }, [dispatch, currentPage, productsPerPage, sortBy, isSearchMode]);

  // Handle advanced search results (client-side pagination)
  useEffect(() => {
    if (advancedSearchResults.length > 0) {
      setDisplayResults(advancedSearchResults);
      setIsSearchMode(true);
    } else if (isSearchMode && advancedSearchResults.length === 0) {
      setDisplayResults([]);
    }
  }, [advancedSearchResults, isSearchMode]);

  // Handle regular products display (backend pagination)
  useEffect(() => {
    if (!isSearchMode && products.length > 0) {
      setPaginatedResults(products); // For regular products, backend handles pagination
    }
  }, [products, isSearchMode]);

  // Handle pagination of search results (client-side pagination)
  useEffect(() => {
    if (isSearchMode && displayResults.length > 0) {
      const startIndex = (currentPage - 1) * productsPerPage;
      const endIndex = startIndex + productsPerPage;
      const paginated = displayResults.slice(startIndex, endIndex);
      setPaginatedResults(paginated);
    } else if (isSearchMode && displayResults.length === 0) {
      setPaginatedResults([]);
    }
  }, [displayResults, currentPage, productsPerPage, isSearchMode]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setCurrentPage(1);
      setIsSearchMode(true);
      updateURL({ q: searchTerm, page: 1 });
      dispatch(
        fetchAdvancedSearch({
          search: searchTerm,
          max_results: 1000,
          fields: ["name", "brand", "description", "keywords", "tags"],
        })
      );
    } else {
      // Clear search and show all products
      setIsSearchMode(false);
      setDisplayResults(products);
      updateURL({ q: null, page: 1 });
      dispatch(fetchAllProductsWithSearch(1, productsPerPage, "", sortBy));
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug || product.product_id}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateURL({ page });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    setShowSortOptions(false);
    updateURL({ sort: newSortBy, page: 1 });
  };

  const handleProductsPerPageChange = (newProductsPerPage) => {
    setProductsPerPage(newProductsPerPage);
    setCurrentPage(1);
    setShowFilterOptions(false);
    updateURL({ perPage: newProductsPerPage, page: 1 });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPage ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {pages}
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    );
  };

  const isLoading = loading || advancedSearchLoading;

  return (
    <div className="all-products-container">
      <div className="all-products-header">
        <h1>All Products</h1>
        {isLoading ? (
          <div className="home-skeleton all-products-skeleton-count" />
        ) : (
          <p className="products-count">
            Showing {(currentPage - 1) * productsPerPage + 1}-
            {Math.min(
              currentPage * productsPerPage,
              isSearchMode ? advancedSearchCount : count
            )}{" "}
            of {isSearchMode ? advancedSearchCount : count} products
          </p>
        )}
      </div>

      <div className="all-products-controls">
        {/* Search Bar */}
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <button type="submit" className="search-button">
              <FaSearch />
            </button>
          </div>
        </form>

        {/* Sort and Filter Controls */}
        <div className="controls-row">
          <div className="control-group">
            <button
              className="control-btn"
              onClick={() => setShowSortOptions(!showSortOptions)}
            >
              <FaSort />
              Sort: {sortOptions.find((opt) => opt.value === sortBy)?.label}
            </button>
            {showSortOptions && (
              <div className="dropdown-menu">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`dropdown-item ${
                      sortBy === option.value ? "active" : ""
                    }`}
                    onClick={() => handleSortChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="control-group">
            <button
              className="control-btn"
              onClick={() => setShowFilterOptions(!showFilterOptions)}
            >
              <FaFilter />
              Show: {productsPerPage} per page
            </button>
            {showFilterOptions && (
              <div className="dropdown-menu">
                {productsPerPageOptions.map((option) => (
                  <button
                    key={option}
                    className={`dropdown-item ${
                      productsPerPage === option ? "active" : ""
                    }`}
                    onClick={() => handleProductsPerPageChange(option)}
                  >
                    {option} per page
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="products-grid">
        {paginatedResults.map((product) => (
          <div
            key={product.product_id}
            className="product-item"
            onClick={() => handleProductClick(product)}
          >
            <ProductCard product={product} />
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          {renderPagination()}
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}

      {/* No Results */}
      {paginatedResults.length === 0 && !isLoading && (
        <div className="no-results">
          <h3>No products found</h3>
          <p>Try adjusting your search terms or filters</p>
        </div>
      )}
    </div>
  );
};

export default AllProducts;
